import * as THREE from 'three';
import { ShadowReach } from '../shadow-reach.js';

/** Ground square, in metres, gathered under one chunk. */
const CHUNK = 48;
/** Room for what moves inside a chunk (rotors, flags, swaying boughs) beyond its bounds at construction. */
const MARGIN = 3;
/** How far small parts placed by the CPU or a shader each frame (a fire's sparks) may stray from where they were built. */
const DRIFT = 6;

/** A patch of the map's still scenery. Skipped whole by every scene walk while neither the view nor the sun can see it. */
class SceneryChunk extends THREE.Group {
  readonly bounds = new THREE.Box3();
  /** Whether anything inside animates its transform; a chunk of fixed scenery never needs its matrices walked again. */
  moving = false;
  override updateMatrixWorld(force?: boolean) { if (this.visible && this.moving) super.updateMatrixWorld(force); }
}

/**
 * The coast holds about 4,300 scenery roots (3,200 instanced batches and 1,000 props), and three walks the whole scene up to
 * three times a frame: world matrices, the view's draw list and the shadow map's. Each walk tests every root even when the
 * view holds a few dozen, which cost about 4 ms of a menu frame. This gathers still scenery roots into chunks by ground
 * square, and before each frame shows only the chunks inside the view or throwing a shadow into it (`ShadowReach`), so the walks skip the rest.
 * Roots that move, hold a light (lights change the shader light count), or have a part drawn without culling stay as they are.
 */
export class SceneryChunks {
  private chunks: SceneryChunk[] = [];
  private reach = new ShadowReach();

  constructor(scene: THREE.Scene, roots: THREE.Object3D[]) {
    const byKey = new Map<string, SceneryChunk>(), box = new THREE.Box3(), centre = new THREE.Vector3();
    for (const root of roots) {
      const fit = root.matrixWorldAutoUpdate || !root.visible ? false : chunkable(root);
      if (!fit) continue;
      box.setFromObject(root);
      if (box.isEmpty()) continue;
      if (fit === 'drifting') box.expandByScalar(DRIFT);
      box.getCenter(centre);
      const key = `${Math.floor(centre.x / CHUNK)}:${Math.floor(centre.z / CHUNK)}`;
      let chunk = byKey.get(key);
      if (!chunk) {
        byKey.set(key, chunk = new SceneryChunk()); chunk.name = 'scenery-chunk';
        chunk.matrixAutoUpdate = false; chunk.matrixWorldAutoUpdate = false; scene.add(chunk); this.chunks.push(chunk);
      }
      chunk.bounds.union(box);
      root.traverse(object => { if (object !== root && object.matrixWorldAutoUpdate) chunk.moving = true; });
      // The chunk sits at the origin, so every world matrix inside stays what it was.
      chunk.add(root);
    }
    for (const chunk of this.chunks) { chunk.bounds.expandByScalar(MARGIN); mergeStill(chunk); }
  }

  /** Before drawing: shows the chunks inside the camera's view or whose shadows reach into it. */
  cull(camera: THREE.Camera, light: THREE.DirectionalLight | null) {
    this.reach.update(camera, light);
    for (const chunk of this.chunks) {
      const visible = this.reach.box(chunk.bounds);
      if (visible && !chunk.visible && chunk.moving) chunk.visible = true, chunk.updateMatrixWorld(true);
      chunk.visible = visible;
    }
  }
}

/**
 * Whether a root can live in a chunk: no lights, points or lines. A mesh drawn without frustum culling is allowed only when
 * small (a fire's sparks), and then the root's bounds grow by `DRIFT`; a large one (the sea, the sky) stays out.
 */
function chunkable(root: THREE.Object3D): false | 'fixed' | 'drifting' {
  let fit: false | 'fixed' | 'drifting' = 'fixed';
  root.traverse(object => {
    const mesh = object as THREE.Mesh;
    if ((object as THREE.Light).isLight || (object as THREE.Points).isPoints || (object as THREE.Line).isLine) fit = false;
    else if (mesh.isMesh && !object.frustumCulled && fit) {
      if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
      fit = mesh.geometry.boundingSphere!.radius < 1 ? 'drifting' : false;
    }
  });
  return fit;
}
/** A still surface this large is already one draw; copying it would only cost memory. */
const MERGE_VERTICES = 20_000;
const plainRender = THREE.Object3D.prototype.onBeforeRender, plainShadow = THREE.Object3D.prototype.onBeforeShadow;

/**
 * Inside a chunk, still meshes that share a material (and shadow flags and vertex layout) are copied in world space into one
 * mesh, so a camp's crates, poles and stones draw in a few calls instead of one each. Normals turn with their mesh and
 * mirrored parts keep their faces' winding. Anything that moves or sits under something that moves, carries its own draw
 * hooks, depth material, morphs, material lists, tangents or interleaved data, or is hidden, keeps its own draw. The originals leave
 * the scene, so code must not show, hide or move a still scenery mesh after the map is built.
 */
function mergeStill(chunk: THREE.Object3D) {
  const groups = new Map<string, THREE.Mesh[]>();
  const visit = (object: THREE.Object3D) => {
    if (!object.visible || object.matrixWorldAutoUpdate) return;
    const mesh = object as THREE.Mesh, key = mesh.isMesh ? mergeKey(mesh) : null;
    if (key) { let list = groups.get(key); if (!list) groups.set(key, list = []); list.push(mesh); }
    for (const child of object.children) visit(child);
  };
  for (const root of chunk.children) visit(root);
  const normal = new THREE.Matrix3(), point = new THREE.Vector3();
  for (const meshes of groups.values()) {
    if (meshes.length < 2) continue;
    const first = meshes[0], names = Object.keys(first.geometry.attributes);
    let vertices = 0, indices = 0;
    for (const { geometry } of meshes) { vertices += geometry.attributes.position.count; indices += geometry.index?.count ?? geometry.attributes.position.count; }
    const merged = new THREE.BufferGeometry(), index = new Uint32Array(indices);
    const arrays = names.map(name => { const source = first.geometry.attributes[name] as THREE.BufferAttribute; return new (source.array.constructor as Float32ArrayConstructor)(vertices * source.itemSize); });
    let v = 0, i = 0;
    for (const mesh of meshes) {
      const { geometry, matrixWorld } = mesh, count = geometry.attributes.position.count, base = v;
      normal.getNormalMatrix(matrixWorld);
      names.forEach((name, a) => {
        const source = geometry.attributes[name] as THREE.BufferAttribute, target = arrays[a], size = source.itemSize;
        for (let k = 0; k < count; k++) {
          if (name === 'position' || name === 'normal') {
            point.fromBufferAttribute(source, k);
            if (name === 'position') point.applyMatrix4(matrixWorld); else point.applyMatrix3(normal).normalize();
            target[(base + k) * 3] = point.x; target[(base + k) * 3 + 1] = point.y; target[(base + k) * 3 + 2] = point.z;
          } else for (let c = 0; c < size; c++) target[(base + k) * size + c] = source.array[k * size + c];
        }
      });
      const mirrored = matrixWorld.determinant() < 0, faces = geometry.index?.count ?? count;
      for (let k = 0; k < faces; k += 3) {
        const a = geometry.index ? geometry.index.getX(k) : k, b = geometry.index ? geometry.index.getX(k + 1) : k + 1, c = geometry.index ? geometry.index.getX(k + 2) : k + 2;
        index[i++] = base + a; index[i++] = base + (mirrored ? c : b); index[i++] = base + (mirrored ? b : c);
      }
      v += count;
    }
    names.forEach((name, a) => { const source = first.geometry.attributes[name] as THREE.BufferAttribute; merged.setAttribute(name, new THREE.BufferAttribute(arrays[a], source.itemSize, source.normalized)); });
    merged.setIndex(new THREE.BufferAttribute(index, 1)); merged.computeBoundingSphere(); merged.computeBoundingBox();
    const batch = new THREE.Mesh(merged, first.material);
    batch.name = 'scenery-merged'; batch.castShadow = first.castShadow; batch.receiveShadow = first.receiveShadow; batch.renderOrder = first.renderOrder;
    batch.matrixAutoUpdate = false; batch.matrixWorldAutoUpdate = false; batch.updateMatrixWorld(true);
    for (const mesh of meshes) mesh.removeFromParent();
    chunk.add(batch);
  }
}

function mergeKey(mesh: THREE.Mesh) {
  const geometry = mesh.geometry, material = mesh.material;
  if ((mesh as THREE.InstancedMesh).isInstancedMesh || (mesh as THREE.SkinnedMesh).isSkinnedMesh || Array.isArray(material) || !mesh.frustumCulled) return null;
  if (mesh.onBeforeRender !== plainRender || mesh.onBeforeShadow !== plainShadow || mesh.customDepthMaterial || mesh.customDistanceMaterial || mesh.morphTargetInfluences) return null;
  // Groups only matter with a list of materials; one material draws the whole geometry.
  if (mesh.layers.mask !== 1 || geometry.drawRange.start !== 0 || geometry.drawRange.count !== Infinity || Object.keys(geometry.morphAttributes).length) return null;
  const position = geometry.attributes.position;
  if (!position || position.count > MERGE_VERTICES || geometry.attributes.tangent) return null;
  const layout: string[] = [];
  for (const [name, attribute] of Object.entries(geometry.attributes)) {
    if ((attribute as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) return null;
    const plain = attribute as THREE.BufferAttribute;
    if ((name === 'position' || name === 'normal') && (plain.itemSize !== 3 || !(plain.array instanceof Float32Array))) return null;
    layout.push(`${name}/${plain.itemSize}/${plain.array.constructor.name}/${plain.normalized}`);
  }
  return `${material.uuid}|${mesh.castShadow}|${mesh.receiveShadow}|${mesh.renderOrder}|${layout.sort().join(',')}`;
}
